import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

import { withAiDescription } from '../../../../../components-shared/Settings/withAiDescription';

export type MenusSettingsParams = {
  showUnavailableOptions: SettingsParamType.Boolean;
  showExpired: SettingsParamType.Boolean;
  showUnavailableAndExpiredLast: SettingsParamType.Boolean;
  sortedMenuIds: SettingsParamType.Text;
};

/**
 * `metadata` exposes the availability params in the widget's editor manifest
 * (`editorElement.data`), which is what Aria reads and edits in Studio 2, and
 * `withAiDescription` gives each one description for both Aria versions.
 * `sortedMenuIds` stays without `metadata`: it stores the menu order as JSON
 * and is changed only through the Settings panel's menu list.
 */
export const menusSettingsParams = createSettingsParams<MenusSettingsParams>({
  showUnavailableOptions: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: ({ t }) => ({
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ...withAiDescription({
        displayName: t('app.settings.menus.show-unavailable-options'),
        description:
          'Shows (true) or hides (false) menus, sections and items that are unavailable for ordering. ' +
          'showExpired and showUnavailableAndExpiredLast only take effect while this is true.',
      }),
    }),
  },
  showExpired: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: ({ t }) => ({
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ...withAiDescription({
        displayName: t('app.settings.menus.show-expired'),
        description:
          'Only while showUnavailableOptions is true: shows (true) or hides (false) unavailable menus and ' +
          'sections that have no upcoming availability (expired). When false, only unavailable ones that ' +
          'become available later are shown.',
      }),
    }),
  },
  showUnavailableAndExpiredLast: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: ({ t }) => ({
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ...withAiDescription({
        displayName: t('app.settings.menus.show-unavailable-and-expired-last'),
        description:
          'Only while showUnavailableOptions is true: lists unavailable and expired menus, sections and ' +
          'items after the available ones (true) or keeps their regular order (false).',
      }),
    }),
  },
  sortedMenuIds: {
    type: SettingsParamType.Text,
    getDefaultValue: () => '',
  },
});
